/**
 * Helle oder dunkle Fassung — und die dritte Möglichkeit, die man leicht
 * vergisst.
 *
 * Drei Zustände, nicht zwei:
 *
 *   hell    ausdrücklich hell, egal was das System sagt
 *   dunkel  ausdrücklich dunkel
 *   system  folgt der Einstellung des Betriebssystems  ← Vorgabe
 *
 * `system` als Vorgabe, und zwar als eigener Zustand statt als „hell, bis
 * jemand etwas anderes wählt". Der Unterschied zeigt sich abends: wer *system*
 * gewählt hat, wird mit dem Rechner dunkel; wer *hell* gewählt hat, bleibt hell.
 * Beides ist eine Aussage, und sie sind nicht dieselbe.
 *
 * Die Wahl liegt in `localStorage` und damit im Browser des Benutzers — nicht am
 * Konto. Das ist Absicht: sie hängt am Gerät und an der Umgebung, in der jemand
 * gerade sitzt, nicht an seiner Person.
 */

export type Thema = 'hell' | 'dunkel' | 'system';

export const THEMEN: Thema[] = ['hell', 'dunkel', 'system'];

const SCHLUESSEL = 'juni.thema';

/** Was gespeichert ist. Unbekanntes wird zu `system`, nicht zu einem Fehler. */
export function gespeichertesThema(): Thema {
  try {
    const wert = localStorage.getItem(SCHLUESSEL);
    return wert === 'hell' || wert === 'dunkel' ? wert : 'system';
  } catch {
    // Privates Fenster, gesperrte Website-Daten — dann eben die Systemvorgabe.
    return 'system';
  }
}

/** Ob es gerade dunkel ist, nachdem `system` aufgelöst wurde. */
export function istDunkel(thema: Thema): boolean {
  if (thema === 'dunkel') return true;
  if (thema === 'hell') return false;
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  );
}

/**
 * Schreibt die Klasse ans `<html>`.
 *
 * Nur diese eine Stelle fasst das DOM an — der Rest der Anwendung weiß nichts
 * davon und liest die Farben über die Tailwind-Klassen, die sie ohnehin
 * benutzt.
 */
export function anwenden(thema: Thema): void {
  const wurzel = document.documentElement;
  wurzel.classList.toggle('dark', istDunkel(thema));
  // Für alles, was nicht über Tailwind läuft — native Bedienelemente,
  // Scrollbalken, die Farbe der Textauswahl.
  wurzel.style.colorScheme = istDunkel(thema) ? 'dark' : 'light';
}

export function themaSpeichern(thema: Thema): void {
  try {
    if (thema === 'system') localStorage.removeItem(SCHLUESSEL);
    else localStorage.setItem(SCHLUESSEL, thema);
  } catch {
    // Nicht speicherbar heißt: gilt für diese Sitzung. Besser als gar nichts.
  }
  anwenden(thema);
}

/**
 * Folgt der Systemeinstellung, solange `system` gewählt ist.
 *
 * Gibt die Abmeldefunktion zurück. Ohne das bliebe die Seite hell, wenn der
 * Rechner abends umschaltet — und genau dafür gibt es diesen Zustand.
 */
export function systemBeobachten(hole: () => Thema): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
  const abfrage = window.matchMedia('(prefers-color-scheme: dark)');
  const bei = () => {
    if (hole() === 'system') anwenden('system');
  };
  abfrage.addEventListener('change', bei);
  return () => abfrage.removeEventListener('change', bei);
}
